import { computed, ref } from 'vue';
import { DateTime } from 'luxon';
import { PlanType } from '@/views/plan/store/types';
import { usePlansStore } from '@/views/plan/store/store';
import { planState } from '@/views/plan/store/planState';
import { trackEvent } from '@/plugins/amplitude';
import { caseEventProperties, planEventProperties } from '@/plugins/amplitudeEvent';
import { useOrganisationProfile } from '@/stores/organisationProfile/store';
import { useUserStore } from '@/stores/userStore';
import { useSurgicalGuideOrders } from '@/stores/surgicalGuideOrders/store';
import { verify } from '@/util/verify';
import type { ApiPlan, ApiPlansCase } from '@/views/plan/api/types';

/** Where a plan is in being made, as the card has to render it. */
export type PlanCreation = 'creating' | 'broken' | 'ready';

/**
 * A plan that never finished.
 *
 * Nothing marks a plan as abandoned, so age stands in for it: one still working a day after it
 * was asked for is not working. An unparseable timestamp is not evidence of that, so it reads as
 * not stale and the card keeps waiting.
 */
export function planIsStale(createdOn: string, now: DateTime = DateTime.utc()): boolean {
    const created = DateTime.fromISO(createdOn, { zone: 'utc' });

    return created.isValid && created.plus({ day: 1 }) < now;
}

/**
 * Whether the plan is still being made, gave up, or is there to download.
 *
 * One answer rather than two booleans the view then has to keep apart: 'creating' and 'broken'
 * are mutually exclusive, and a template asking each in turn cannot say so.
 */
export function planCreation(state: string, stale: boolean): PlanCreation {
    const resourceState = planState(state);

    if (resourceState === 'error' || (resourceState !== 'completed' && stale)) {
        return 'broken';
    }

    if (resourceState === 'new' || resourceState === 'processing') {
        return 'creating';
    }

    return 'ready';
}

type OrderSituation = {
    /** A guide is cut to this plan, so it needs the surgeon's decision behind it. */
    approved: boolean;

    /** Whoever may order on a role alone. */
    mayOrderByRole: boolean;
    isSales: boolean;
    isManualPlan: boolean;
    hasOrder: boolean;
};

/**
 * Whether this caller may order a guide against this plan.
 *
 * The API refuses an order against a proposal, so an unapproved plan offers nothing to anybody -
 * this keeps the caller from meeting that refusal after clicking. A sales rep reaches the button
 * for a plan of a kind they prepare, or one they have already ordered against so they can act on
 * it; that arm is about the plan rather than the caller, which is why it is not a role flag.
 */
export function mayOrderGuide(situation: OrderSituation): boolean {
    const { approved, mayOrderByRole, isSales, isManualPlan, hasOrder } = situation;

    return approved && (mayOrderByRole || (isSales && (isManualPlan || hasOrder)));
}

type Plan = () => ApiPlan;
type Project = () => ApiPlansCase;
type PlansStore = ReturnType<typeof usePlansStore>;

/** Which buttons the card offers, and to whom. */
function planActionsOnOffer(plan: Plan) {
    const organisationProfile = useOrganisationProfile();
    const userStore = useUserStore();
    const orders = useSurgicalGuideOrders();

    return {
        showReportDownload: computed(() => plan().report !== null),
        showModelsDownload: computed(() => plan().files.length > 0),

        showOrderGuide: computed(
            () =>
                organisationProfile.features.order_surgical_guide &&
                plan().report !== null &&
                mayOrderGuide({
                    approved: plan().approval_state === 'approved',
                    mayOrderByRole: userStore.mayOrderSurgicalGuide,
                    isSales: userStore.isSales,
                    isManualPlan: plan().type === PlanType.Manual,
                    hasOrder: orders.getPlanOrder(plan().id) !== null,
                }),
        ),

        /**
         * The warning belongs to the manual plan alone: it names changes made in the 3D planner
         * since the plan was produced, and the automated plan is not made from that template.
         */
        showDownloadWarning: computed(() => {
            const report = plan().report;

            return plan().type === PlanType.Manual && report !== null
                ? !report.include_latest_changes
                : false;
        }),
    };
}

/** Taking a copy of a plan, and saying so. */
function planDownloads(plan: Plan, project: Project, store: PlansStore) {
    const isDownloadingPdf = ref(false);
    const isDownloadingModels = ref(false);
    const showWarningDialog = ref(false);
    const isAutomated = () => plan().type === PlanType.Automated;

    const track = (event: 'plan_downloaded' | 'models_downloaded') =>
        trackEvent(event, {
            ...caseEventProperties({ projectId: project().id.toString() }),
            ...planEventProperties(plan().id.toString(), isAutomated()),
        });

    const downloadReport = async () => {
        isDownloadingPdf.value = true;
        try {
            await (isAutomated() ? store.downloadAutomatedPlan() : store.downloadManualPlan());
        } finally {
            await track('plan_downloaded');
            isDownloadingPdf.value = false;
        }
    };

    return {
        isDownloadingPdf,
        isDownloadingModels,
        showWarningDialog,

        /**
         * Download the report, or ask first when the plan no longer holds the latest changes.
         *
         * The automated plan never asks - there is no 3D-planner edit it could be behind.
         */
        requestReportDownload: async () => {
            const report = isAutomated()
                ? null
                : verify(plan().report, 'manual plan download with no report');

            if (report && !report.include_latest_changes) {
                showWarningDialog.value = true;
            } else {
                await downloadReport();
            }
        },

        downloadFromWarning: async () => {
            showWarningDialog.value = false;
            await downloadReport();
        },

        closeWarning: () => {
            showWarningDialog.value = false;
        },

        downloadModels: async () => {
            isDownloadingModels.value = true;
            try {
                await store.downloadModels(plan());
            } finally {
                await track('models_downloaded');
                isDownloadingModels.value = false;
            }
        },
    };
}

export function usePlanActionsViewModel(plan: Plan, project: Project) {
    return {
        creation: computed(() => planCreation(plan().state, planIsStale(plan().created_on))),
        ...planActionsOnOffer(plan),
        ...planDownloads(plan, project, usePlansStore()),
    };
}
